<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, onMounted, ref } from 'vue'
import { useFavoritesStore } from '@stores/favorites'
import MixTableBlock from '@components/mix-table-block.v2'
import LibrarySectionTabs from '@components/library/library-section-tabs'
import LibraryHeader from '@components/library/library-header'
import LibrarySearchResults from '@components/library/library-search-results'
import LikedMixesCard from '@components/library/liked-mixes-card'

const RECENT_ACTIVITY_LIMIT = 10

const favoritesStore = useFavoritesStore()
const searchQuery = ref('')
const likedMixes = computed(() => favoritesStore.likedMixes)
const mixesLoaded = computed(() => favoritesStore.isLoaded('mixes'))
const recentActivity = computed(() => likedMixes.value.slice(0, RECENT_ACTIVITY_LIMIT))

onMounted(() => {
    // background refresh of what this page renders (mixes only) — favorites/init is
    // unguarded and would refire all four limit-100000 fetches on every visit
    favoritesStore.fetchType('mixes').catch(error => {
        // leave `loaded` untouched — the spinner (not a false empty state) is the FR21 failure mode
        console.error('Failed to refresh liked mixes', error)
    })
})

usePageHead({ title: 'Your Library' })
</script>

<template>
    <BaseContent>
        <BaseSection padded>
            <LibraryHeader
                title="Your Library"
                :search-query="searchQuery"
                @update:search-query="searchQuery = $event"
            />
            <LibrarySearchResults
                v-if="searchQuery.trim()"
                :query="searchQuery"
            />
            <template v-else>
                <LibrarySectionTabs />
                <LikedMixesCard :count="mixesLoaded ? likedMixes.length : null" />
                <template v-if="recentActivity.length">
                    <h2 class="recent-title">Recently Added</h2>
                    <MixTableBlock
                        :mixes="recentActivity"
                        genres
                    />
                </template>
                <BaseSpinner v-else-if="!mixesLoaded" />
                <BaseEmptyList
                    v-else
                    :error="{
                        title: 'No Mixes Yet',
                        paragraph: 'Like a mix? Tap the ♥ to add it to this list.',
                    }"
                />
            </template>
        </BaseSection>
    </BaseContent>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.recent-title {
    @include sectionHeading;

    margin-bottom: $s-s;
}
</style>
